<template>
  <div ref="slider" :class="Boolean(pips) ? '' : 'slider-hide-ring'"></div>
  <slot :update="update" :set-handle="setHandle" />
</template>

<script setup lang="ts">
import { API, create, PipsMode, target, Formatter } from "nouislider";

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    min?: number;
    max?: number;
    step?: number | undefined;
    modelValue: (string | number)[];
    pips?: boolean;
    format?: Formatter;
    margin?: number;
    behaviour?: string;
    minPadding?: number;
    maxPadding?: number;
  }>(),
  {
    min: 0,
    max: 100,
    minPadding: 0,
    maxPadding: 0,
    step: undefined,
    format: undefined,
    margin: 0,
    behaviour: "tap",
  },
);

const { disabled } = toRefs(props);

const emit = defineEmits(["update:modelValue", "update"]);

const slider = ref<target | null>(null);

const $slider = ref<API | null>(null);

watch(disabled, () => {
  if ($slider.value) {
    if (disabled.value) {
      $slider.value.disable();
    } else {
      $slider.value.enable();
    }
  }
});

watch(
  () => props.modelValue,
  (val) => {
    $slider.value?.set(val);
  },
);

function init() {
  if (slider.value) {
    $slider.value = create(slider.value, {
      start: isEmpty(props.modelValue)
        ? [props.min, props.max]
        : props.modelValue,
      behaviour: props.behaviour,
      connect: true,
      range: {
        min: props.min,
        max: props.max,
      },
      padding: [props.minPadding, props.maxPadding],
      step: props.step,
      margin: props.margin,
      pips: props.pips
        ? {
            mode: PipsMode.Steps,
            density: 100,
            filter: () => {
              return 0;
            },
          }
        : undefined,
      cssPrefix: "",
      cssClasses: {
        target: "slider-target",
        base: "slider-base",
        origin: "slider-origin",
        handle: "slider-handle",
        handleLower: "slider-handle-lower",
        handleUpper: "slider-handle-upper",
        touchArea: "slider-touch-area",
        horizontal: "slider-horizontal",
        vertical: "slider-vertical",
        background: "slider-background",
        connect: "slider-connect",
        connects: "slider-connects",
        ltr: "slider-ltr",
        rtl: "slider-rtl",
        textDirectionLtr: "slider-txt-dir-ltr",
        textDirectionRtl: "slider-txt-dir-rtl",
        draggable: "slider-draggable",
        drag: "slider-state-drag",
        tap: "slider-state-tap",
        active: "slider-active",
        tooltip: "slider-tooltip",
        pips: "slider-pips",
        pipsHorizontal: "slider-pips-horizontal",
        pipsVertical: "slider-pips-vertical",
        marker: "slider-marker",
        markerHorizontal: "slider-marker-horizontal",
        markerVertical: "slider-marker-vertical",
        markerNormal: "slider-marker-normal",
        markerLarge: "slider-marker-large",
        markerSub: "slider-marker-sub",
        value: "slider-value",
        valueHorizontal: "slider-value-horizontal",
        valueVertical: "slider-value-vertical",
        valueNormal: "slider-value-normal",
        valueLarge: "slider-value-large",
        valueSub: "slider-value-sub",
      },
      format: {
        to(value) {
          return Number(value.toFixed(2));
        },
        from(value) {
          return Number(value);
        },
      },
    });

    $slider.value.on("set", (values) => {
      if (!isEqual(props.modelValue, values)) {
        emit("update:modelValue", values);
      }
    });

    $slider.value.on("update", (values) => {
      if (!isEqual(props.modelValue, values)) {
        emit("update", values);
      }
    });
    if (disabled.value) {
      $slider.value.disable();
    }
  }
}

function update(
  val: string | number | (string | number)[],
  triggerChange = true,
) {
  if ($slider.value) {
    $slider.value.set(val, triggerChange);
  }
}

function setHandle(value: number | string, handle: number) {
  if ($slider.value) {
    $slider.value.setHandle(handle, value, true);
  }
}

const destroy = () => {
  if (!$slider.value) return;
  $slider.value.off("");
  $slider.value.destroy();
  $slider.value = null;
};
onMounted(init);
onUnmounted(destroy);

defineExpose({
  setHandle,
  update,
});
</script>

<style>
.slider-target {
  @apply relative isolate box-border touch-none select-none px-3.5 tap-highlight-transparent touch-callout-none slider-disabled:cursor-not-allowed;
}

.slider-horizontal {
  @apply my-3 h-1;
}

.slider-base {
  @apply relative h-full;
}

.slider-connects {
  @apply relative z-0 -mx-3.5 h-full overflow-hidden bg-gray-60;
}

.slider-connect {
  @apply absolute right-0 top-0 z-[1] h-full w-full cursor-pointer bg-orange-60 transform-origin-0 transform-style-flat tap:transition-transform tap:duration-300 slider-disabled:cursor-not-allowed slider-disabled:bg-gray-details;
}

.slider-origin {
  @apply pointer-events-none absolute right-0 top-0 z-[1] h-full w-full transform-origin-0 transform-style-flat tap:transition-transform tap:duration-300;
}

.slider-handle {
  @apply absolute -right-3.5 -top-3 z-[2] size-7 cursor-grab rounded-full border-0 border-2 border-orange-60 bg-white shadow-handle ring-4 ring-white pointer-events-all focus:outline-none hide-ring:ring-0 slider-disabled:cursor-not-allowed slider-disabled:bg-gray-details;
}

.slider-handle .slider-touch-area {
  @apply h-full w-full;
}

.slider-pips {
  @apply pointer-events-none flex justify-between overflow-hidden text-xs font-normal text-gray-elements;
}

.slider-pips-horizontal {
  @apply absolute left-0 top-1/2 z-0 h-[calc(100%+2px)] w-full -translate-y-1/2;
}

.slider-pips-vertical {
  @apply left-full top-0 h-full flex-col;
}

.slider-marker {
  left: auto !important;
  @apply pointer-events-none bg-white;
}

.slider-marker-horizontal {
  @apply h-full w-[5px] first:opacity-0 last:opacity-0;
}
</style>
